import React from 'react';

interface Props {
  data: any[];
}

export default function TurnMetricsChart({ data }: Props) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground">
        No metrics data available
      </div>
    );
  }

  const maxSessions = Math.max(...data.map(d => d.activeSessions || 0), 1);
  const maxBandwidth = Math.max(
    ...data.map(d => Math.max(d.bandwidthInMbps || 0, d.bandwidthOutMbps || 0)),
    0.1
  );

  const sampleRate = Math.max(1, Math.floor(data.length / 60));
  const sampled = data.filter((_, i) => i % sampleRate === 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Active Sessions Chart */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="text-sm text-muted-foreground mb-3">Active Sessions</h3>
        <div className="flex items-end gap-px h-32">
          {sampled.map((d, i) => (
            <div
              key={i}
              className="bg-emerald-500/80 rounded-t flex-1 min-w-[2px] hover:bg-emerald-400 transition-colors"
              style={{ height: `${(d.activeSessions / maxSessions) * 100}%` }}
              title={`${d.activeSessions} sessions at ${new Date(d.timestamp).toLocaleTimeString()}`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{sampled.length > 0 ? new Date(sampled[0].timestamp).toLocaleTimeString() : ''}</span>
          <span>{sampled.length > 0 ? new Date(sampled[sampled.length - 1].timestamp).toLocaleTimeString() : ''}</span>
        </div>
      </div>

      {/* Bandwidth Chart */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="text-sm text-muted-foreground mb-3">
          Bandwidth <span className="text-cyan-500">In</span> / <span className="text-purple-500">Out</span> (Mbps)
        </h3>
        <div className="flex items-end gap-px h-32">
          {sampled.map((d, i) => (
            <div key={i} className="flex-1 min-w-[2px] flex flex-col justify-end gap-0">
              <div
                className="bg-cyan-500/80 rounded-t"
                style={{ height: `${(d.bandwidthInMbps / maxBandwidth) * 50}%` }}
                title={`In: ${d.bandwidthInMbps?.toFixed(2)} Mbps`}
              />
              <div
                className="bg-purple-500/80"
                style={{ height: `${(d.bandwidthOutMbps / maxBandwidth) * 50}%` }}
                title={`Out: ${d.bandwidthOutMbps?.toFixed(2)} Mbps`}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{sampled.length > 0 ? new Date(sampled[0].timestamp).toLocaleTimeString() : ''}</span>
          <span>{sampled.length > 0 ? new Date(sampled[sampled.length - 1].timestamp).toLocaleTimeString() : ''}</span>
        </div>
      </div>
    </div>
  );
}
